import React from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Home,
  Flame,
  Camera,
  Upload,
  User,
  Settings,
  MapPin,
  ShieldAlert,
  ClipboardList,
  SlidersHorizontal,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { clsx } from 'clsx';

const usePendingCount = () => {
  const { data } = useQuery(
    'moderation-pending-count',
    () => apiService.getModerationPendingCount(),
    { refetchInterval: 30_000, retry: false },
  );
  return data?.count ?? null;
};

const NavItem = ({ item, onClose }) => (
  <NavLink
    key={item.name}
    to={item.href}
    end={item.end}
    onClick={onClose}
    className={({ isActive }) =>
      clsx(
        'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
        isActive
          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
      )
    }
  >
    <item.icon className="w-5 h-5 mr-3" />
    <span className="flex-1">{item.name}</span>
    {item.badge != null && item.badge > 0 && (
      <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full bg-fire-500 text-white">
        {item.badge}
      </span>
    )}
  </NavLink>
);

const Sidebar = ({ isOpen, onClose }) => {
  const { user, isAdmin } = useAuth();
  const pendingCount = usePendingCount();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Incêndios', href: '/fires', icon: Flame },
    { name: 'Mapa', href: '/map', icon: MapPin },
    { name: 'Galeria', href: '/photos', icon: Camera },
    { name: 'Enviar Foto', href: '/upload', icon: Upload },
    { name: 'Perfil', href: '/profile', icon: User },
  ];

  const moderationNav = [
    { name: 'Fila', href: '/moderation/queue', icon: ShieldAlert, badge: pendingCount },
    { name: 'Auditoria', href: '/moderation/audit', icon: ClipboardList },
  ];
  if (isAdmin?.()) {
    moderationNav.push({ name: 'Configurações', href: '/moderation/settings', icon: SlidersHorizontal });
  }

  if (isAdmin?.()) {
    navigation.push({ name: 'Administração', href: '/admin', icon: Settings });
  }

  const renderNav = () => (
    <nav className="flex-1 px-4 py-6 space-y-1">
      {navigation.map((item) => (
        <NavItem key={item.name} item={item} onClose={onClose} />
      ))}

      <div className="pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Moderação fogos.pt
        </div>
        {moderationNav.map((item) => (
          <NavItem key={item.name} item={item} onClose={onClose} />
        ))}
      </div>
    </nav>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:pt-16">
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          {renderNav()}

          {/* User info at bottom */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name} {user?.surname}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.entity || user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 transform transition-transform duration-300 ease-in-out lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <img
                src="/firewatch_logo_transparent.png"
                alt="FIREWATCH"
                className="w-6 h-6 mr-2"
              />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                FIREWATCH
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {renderNav()}

          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name} {user?.surname}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.entity || user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
